import { AnimatePresence } from 'motion/react';

import { Children, createContext, useContext, type ReactElement } from 'react';

import { cn } from '@/shared/utils/cn';

import { MotionDiv } from '../motion-div';
import { type SceneElement, type StageProps, type StageSceneProps } from './types/types';

const StageContext = createContext<{
  className?: string;
}>({});

function isSceneElement(child: unknown): child is SceneElement {
  if (!child || typeof child !== 'object') return false;
  return (child as ReactElement).type === Scene;
}

function Stage({ children, className }: StageProps) {
  const childrenArray = Children.toArray(children);

  if (process.env.NODE_ENV === 'development') {
    childrenArray.forEach((child) => {
      if (!isSceneElement(child)) {
        console.warn('Stage only accepts Stage.Scene as children');
      }
    });
  }

  let sceneToRender = childrenArray.find((child) => isSceneElement(child) && child.props.status);

  if (!sceneToRender) {
    sceneToRender = childrenArray.find((child) => isSceneElement(child) && child.props.default);
  }

  return (
    <StageContext.Provider value={{ className }}>
      <AnimatePresence mode="wait">{sceneToRender || null}</AnimatePresence>
    </StageContext.Provider>
  );
}

function Scene<T>(props: StageSceneProps<T>) {
  const { className } = useContext(StageContext);

  if (!props.children) return null;
  if (!props.status && !props.default) {
    return props.fallback ? props.fallback : null;
  }

  const { ref } = props;
  const data = props.data ?? (typeof props.status !== 'boolean' ? props.status : undefined);

  return (
    <MotionDiv className={cn(className, props.className)} options={props.options} ref={ref}>
      {typeof props.children === 'function'
        ? data
          ? props.children(data as NonNullable<T>)
          : null
        : props.children}
    </MotionDiv>
  );
}

Stage.Scene = Scene;

export { Scene, Stage };
